import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { configs } from '../app/app-configs';
import ScrollToTop from './scroll-to-top';
import logo from "../resources/images/logo.png";
import { MenuIcon } from '../shared/icons';
import { motion } from 'framer-motion';

const sections = ['home', 'pricing', 'features'];

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}

const drawerWidth = 240;

// const StyledMenu = styled(MenuItem)(({ theme }) => ({
//   padding: 0,
//   '& .MuiMenuItem-root': {
//     paddingTop: "12px",
//     paddingBottom: "12px",
//     color: theme.palette.primary.main,
//   },
//   '& .MuiListItemIcon-root': {
//     marginRight: "20px",
//   },
//   '& .Mui-selected': {
//     backgroundColor: theme.palette.primary.main,
//     color: theme.palette.primary.contrastText,
//     '& .MuiSvgIcon-root': {
//       color: theme.palette.primary.contrastText,
//     },
//     '&:hover': {
//       backgroundColor: theme.palette.primary.main,
//       color: theme.palette.primary.contrastText,
//     }
//   },
// }));

export default function Nav(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("home");

  const target = window ? window() : globalThis.window;
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  React.useEffect(() => {
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = () => {

    const scrollPosition = target.scrollY + 64;

    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) {
        const offsetTop = element.offsetTop;
        const height = element.offsetHeight;
        if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
          setActiveSection(section);
        }
      }
    })
  }

  const handleScrollTo = (targetId: string) => {
     if (!target) return;
    // const targetElement = document.getElementById(targetId);
    // if (targetElement) {
    //   target.scrollTo({
    //     top: targetElement.offsetTop - 64,
    //     behavior: 'smooth'
    //   })
    // }

    // If we're not on the home page, navigate there first, then scroll
    if (location.pathname !== '/') {
      // navigate('/', { replace: false });
      // // small delay to allow the Home content to mount, then scroll and set active
      // setTimeout(() => {
      //   const el = document.getElementById(targetId);
      //   if (el) {
      //     // target.scrollTo({ top: el.offsetTop - 64, behavior: 'smooth' });
      //     setActiveSection(targetId);
      //   }
      // }, 120);
      navigate('/', { replace: false, state: { scrollTo: targetId } });
      return;
    }

    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      target.scrollTo({
        top: targetElement.offsetTop - 56,
        behavior: 'smooth'
      });
      // immediately mark as active so nav highlights without needing extra scroll
      // setActiveSection(targetId);
    }
  }

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, color: "#641197" }}>
        {configs.APP_NAME}
      </Typography>
      <Divider />
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}

        onClick={(e) => {
          e.preventDefault();
          // handleDrawerToggle();
          handleScrollTo('home');
        }}
        href="#home"
        className={`text-black ${(activeSection === 'home' && location.pathname === '/') ? 'active-mobile-nav-link' : 'inactive-mobile-nav-link'}`}
      >
        <div className='mobile-nav-link'>HOME</div>
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}

        onClick={(e) => {
          e.preventDefault();
          // handleDrawerToggle();
          handleScrollTo('features');
        }}
        href="#features"
        className={`text-black ${(activeSection === 'features' && location.pathname === '/') ? 'active-mobile-nav-link' : 'inactive-mobile-nav-link'}`}
      >
        <div className='mobile-nav-link'>FEATURES</div>
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}

        onClick={(e) => {
          e.preventDefault();
          // handleDrawerToggle();
          handleScrollTo('pricing');
        }}
        href="#pricing"
        className={`text-black ${(activeSection === 'pricing' && location.pathname === '/') ? 'active-mobile-nav-link' : 'inactive-mobile-nav-link'}`}
      >
        <div className='mobile-nav-link'>PRICING</div>
      </motion.a>

      <div className='mobile-nav-link'><NavLink to="/login" className={({ isActive }) => isActive ? "active-mobile-nav-link" : "inactive-mobile-nav-link"}>LOGIN</NavLink></div>
      {/* <List className={classes.menu}>
        <MenuItem sx={{ cursor: 'pointer' }} onClick={() => onSelectItem("/")}>
          <ListItemText primary={"Home"}></ListItemText>
        </MenuItem>
        <MenuItem sx={{ cursor: 'pointer' }} onClick={() => onSelectItem("/pricing")}>
          <ListItemText primary={"Pricing"} />
        </MenuItem>
        <MenuItem sx={{ cursor: 'pointer' }} onClick={() => onSelectItem("/features")}>
          <ListItemText primary={"Features"} />
        </MenuItem>
        <MenuItem sx={{ cursor: 'pointer' }} onClick={() => onSelectItem("/login")}>
          <ListItemText primary={"Login"} />
        </MenuItem>
      </List> */}
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" sx={{ backgroundColor: "white" }} elevation={0}>
        <Toolbar>
          <IconButton
            color="default"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <img src={logo} style={{ maxWidth: "24px" }} alt="" />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: "pointer", color: "#641197" }}
            onClick={() => navigate('/')}
          >
            yminify
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <motion.a
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}

              onClick={(e) => {
                e.preventDefault();
                // handleDrawerToggle();
                handleScrollTo('home');
              }}
              href="#home"
              className={`text-black ${(activeSection === 'home' && location.pathname === '/') ? 'active-nav-link' : 'inactive-nav-link'}`}>
              HOME
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}

              onClick={(e) => {
                e.preventDefault();
                // handleDrawerToggle();
                handleScrollTo('features');
              }}
              href="#features"
              className={`text-black ${(activeSection === 'features' && location.pathname === '/') ? 'active-nav-link' : 'inactive-nav-link'}`}>
              FEATURES
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}

              onClick={(e) => {
                e.preventDefault();
                // handleDrawerToggle();
                handleScrollTo('pricing');
              }}
              href="#pricing"
              className={`text-black ${(activeSection === 'pricing' && location.pathname === '/') ? 'active-nav-link' : 'inactive-nav-link'}`}>
              PRICING
            </motion.a>
            {/* <NavLink to="/" className={({ isActive }) => isActive ? "active-nav-link" : "inactive-nav-link"}>HOME</NavLink>
            <NavLink to="/pricing" className={({ isActive }) => isActive ? "active-nav-link" : "inactive-nav-link"}>PRICING</NavLink>
            <NavLink to="/features" className={({ isActive }) => isActive ? "active-nav-link" : "inactive-nav-link"}>FEATURES</NavLink> */}
            <NavLink to="/login" className={({ isActive }) => isActive ? "active-nav-link" : "inactive-nav-link"}>LOGIN</NavLink>
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Box component="main" sx={{ width: '100%' }}>
        <Toolbar />
        <ScrollToTop />
        <Outlet />
      </Box>
    </Box>
  );
}